/**
 * SRES · Auto-cutoff Panel — Sprint P37z UI (Feb 2026)
 * ---------------------------------------------------
 * Consumes the existing backend endpoint
 *   POST /api/projects/{pid}/cutoff/calc
 * with an interactive sensitivity slider (0-30 %) so the CP can see
 * how much the cutoff moves under low/base/high-case assumptions.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Calculator, Info } from "@phosphor-icons/react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { WizardCard, KV } from "@/pages/wizard/_shared";

const COMMODITIES = [
  ["gold",     "Gold (g/t)"],
  ["silver",   "Silver (g/t)"],
  ["copper",   "Copper (%)"],
  ["nickel",   "Nickel (%)"],
  ["zinc",     "Zinc (%)"],
  ["lead",     "Lead (%)"],
  ["cobalt",   "Cobalt (%)"],
  ["iron_ore", "Iron Ore (%)"],
  ["graphite", "Graphite (%)"],
  ["uranium",  "Uranium (ppm)"],
  ["lithium",  "Lithium (%)"],
  ["manganese","Manganese (%)"],
  ["chromite", "Chromite (%)"],
];

export default function AutoCutoffPanel({ projectId, defaultCommodity }) {
  const [form, setForm] = useState({
    commodity:       (defaultCommodity || "gold").toLowerCase(),
    price:           "",
    recovery:        0.92,
    mining_cost:     4.5,
    processing_cost: 22.0,
    ga_cost:         1.5,
    selling_cost:    0.0,
    royalty_pct:     0.04,
    tax_pct:         0.0,
    exchange_rate:   1.0,
  });
  const [sensitivity, setSensitivity] = useState(0.15);
  const [result, setResult] = useState(null);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const calcMut = useMutation({
    mutationFn: (body) =>
      api.post(`/projects/${projectId}/cutoff/calc`, body).then((r) => r.data),
    onSuccess: (data) => setResult(data),
    onError: (e) => toast.error(
      e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message
    ),
  });

  const handleRun = () => {
    const body = { ...form, sensitivity_pct: sensitivity };
    // Coerce numeric strings to numbers where the backend expects them
    for (const k of ["price","recovery","mining_cost","processing_cost",
                             "ga_cost","selling_cost","royalty_pct","tax_pct","exchange_rate"]) {
      if (body[k] !== "" && body[k] !== null && body[k] !== undefined) {
        body[k] = Number(body[k]);
      } else {
        delete body[k];
      }
    }
    calcMut.mutate(body);
  };

  return (
    <WizardCard title="Auto-cutoff Grade — commodity-aware economics"
                    testId="acp-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left: inputs */}
        <div className="space-y-3">
          <label className="block">
            <Label className="text-[11px] font-semibold text-slate-600">Commodity</Label>
            <Select value={form.commodity} onValueChange={(v) => patch("commodity", v)}>
              <SelectTrigger data-testid="acp-commodity" className="h-9 text-[12.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMMODITIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <NumField label="Price (metal-specific)"       k="price"           form={form} onChange={patch}
                          placeholder="e.g. 2200 USD/oz" testId="acp-price" />
            <NumField label="Recovery (0-1)"                 k="recovery"        form={form} onChange={patch}
                          step="0.01" testId="acp-recovery" />
            <NumField label="Mining cost ($/t)"              k="mining_cost"     form={form} onChange={patch}
                          testId="acp-mining" />
            <NumField label="Processing cost ($/t)"          k="processing_cost" form={form} onChange={patch}
                          testId="acp-processing" />
            <NumField label="G&A cost ($/t)"                  k="ga_cost"         form={form} onChange={patch}
                          testId="acp-ga" />
            <NumField label="Selling cost ($/product-unit)" k="selling_cost"    form={form} onChange={patch}
                          testId="acp-selling" />
            <NumField label="Royalty (fraction)"             k="royalty_pct"     form={form} onChange={patch}
                          step="0.01" testId="acp-royalty" />
            <NumField label="Tax (fraction)"                  k="tax_pct"         form={form} onChange={patch}
                          step="0.01" testId="acp-tax" />
          </div>

          <div>
            <Label className="text-[11px] font-semibold text-slate-600">
              Sensitivity range · ±{(sensitivity * 100).toFixed(0)} %
            </Label>
            <Slider
              min={0} max={0.3} step={0.01}
              value={[sensitivity]}
              onValueChange={([v]) => setSensitivity(v)}
              data-testid="acp-sensitivity"
              className="mt-1.5"
            />
            <div className="mt-1 text-[10.5px] text-slate-400">
              Move the slider to see how much the cutoff drifts when
              prices and costs swing ±{(sensitivity * 100).toFixed(0)}%.
            </div>
          </div>

          <Button
            onClick={handleRun}
            disabled={calcMut.isPending}
            data-testid="acp-calc"
            className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white gap-1.5"
          >
            <Calculator size={13} weight="fill" />
            {calcMut.isPending ? "Calculating…" : "Compute cutoff"}
          </Button>
        </div>

        {/* Right: results */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500">
            <Info size={12} /> Cutoff cases
          </div>
          {!result && (
            <div className="text-[12px] text-slate-500 italic border border-dashed border-slate-300 rounded p-3">
              Set the inputs and click "Compute cutoff" to see the base
              case and low / high sensitivity variants.
            </div>
          )}
          {result && (
            <div className="border border-slate-200 rounded p-3 bg-slate-50 space-y-1"
                    data-testid="acp-result">
              <KV k="Base cutoff"
                     v={<span className="font-semibold text-brand-blue">{fmt(result.cutoff_base, 3)} {result.unit}</span>} />
              <KV k="Low case (-)"  v={`${fmt(result.cutoff_low, 3)} ${result.unit}`} />
              <KV k="High case (+)" v={`${fmt(result.cutoff_high, 3)} ${result.unit}`} />
              <KV k="Conversion factor" v={fmt(result.conversion_factor, 6)} />
              <KV k="Confidence" v={result.confidence != null ? `${(result.confidence * 100).toFixed(0)}%` : "—"} />
              {result.formula && (
                <div className="mt-2 text-[10.5px] font-mono text-slate-500 whitespace-pre-wrap break-all"
                        data-testid="acp-formula">
                  {result.formula}
                </div>
              )}
              {result.notes?.length > 0 && (
                <ul className="mt-2 text-[10.5px] text-slate-600 list-disc list-inside space-y-0.5">
                  {result.notes.map((n, i) => (
                    <li key={i}>{typeof n === "string" ? n : JSON.stringify(n)}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </WizardCard>
  );
}

function NumField({ label, k, form, onChange, step, placeholder, testId }) {
  return (
    <label className="block">
      <Label className="text-[11px] font-semibold text-slate-600">{label}</Label>
      <Input
        type="number" step={step || "0.01"}
        value={form[k] ?? ""}
        onChange={(e) => onChange(k, e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
      />
    </label>
  );
}

function fmt(v, d = 2) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
}
